import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileEntradaTest = () => {
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    unit_cost: '',
    reason: '',
    photo: '',
    ativo: ''
  });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    }
  };

  const handlePhotoCapture = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor, selecione apenas imagens');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Imagem muito grande. Máximo 5MB');
        return;
      }

      setIsUploadingPhoto(true);
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await axios.post(`${API}/upload-photo`, formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const photoUrl = response.data.photo_url;
        setFormData(prev => ({ ...prev, photo: photoUrl }));
        setPhotoPreview(photoUrl);
        toast.success('Foto capturada com sucesso!');
      } catch (error) {
        console.error('Erro ao fazer upload da foto:', error);
        toast.error('Erro ao processar foto');
      } finally {
        setIsUploadingPhoto(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/stock-movements/entrada`, formData);
      toast.success('Entrada registrada com sucesso!');
      setFormData({ product_id: '', quantity: '', unit_cost: '', reason: '', photo: '', ativo: '' });
      setPhotoPreview(null);
    } catch (error) {
      toast.error('Erro ao registrar entrada');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm px-4 py-4 flex items-center space-x-3">
        <h1 className="text-xl font-bold text-gray-900">📥 Teste Entrada - Mobile</h1>
      </div>

      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Produto</label>
            <select
              value={formData.product_id}
              onChange={(e) => setFormData({...formData, product_id: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Selecione um produto</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name} - {product.code}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">📷 Foto do Produto</label>
            <div className="space-y-3">
              <label className="block w-full cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  capture="camera"
                  onChange={handlePhotoCapture}
                  className="hidden"
                />
                <div className="w-full py-4 px-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 transition-colors">
                  <div className="text-center">
                    <span className="text-3xl">📷</span>
                    <p className="text-sm font-medium text-gray-700 mt-2">
                      {isUploadingPhoto ? 'Processando foto...' : 'Tocar para tirar foto'}
                    </p>
                    <p className="text-xs text-gray-500">Câmera será ativada automaticamente</p>
                  </div>
                </div>
              </label>
              
              {photoPreview && (
                <div className="flex justify-center">
                  <img 
                    src={photoPreview} 
                    alt="Foto capturada" 
                    className="max-w-48 max-h-48 object-cover rounded-lg border shadow-sm"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ativo</label>
            <input
              type="number"
              step="0.01"
              value={formData.ativo}
              onChange={(e) => setFormData({...formData, ativo: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Valor numérico"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              🏢 Estoque/Local <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.storage_id}
              onChange={(e) => setFormData({...formData, storage_id: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Selecione o estoque</option>
              {storages.map((storage) => (
                <option key={storage.id} value={storage.id}>
                  🏢 {storage.name}{storage.location ? ` - 📍 ${storage.location}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade</label>
              <input
                type="number"
                step="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Custo (R$)</label>
              <input
                type="number"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) => setFormData({...formData, unit_cost: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
            <input
              type="text"
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Compra, Devolução"
              required
            />
          </div>

          <button
            type="submit"
            className="w-full bg-green-600 text-white py-4 rounded-lg font-medium text-lg hover:bg-green-700"
            disabled={isUploadingPhoto}
          >
            📥 Registrar Entrada
          </button>
        </form>
      </div>
    </div>
  );
};

export default MobileEntradaTest;